import { html, nothing } from '../application_element/element.js';
import ChartElement from '../chart_element/element.js';
import { formatValue } from '../../lib/chart_format.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * A series or timeseries as a table. Doubles as the accessible alternative to
 * every chart, so the same rows a chart draws can always be read as text.
 *
 * @prop labelHeading - Heading for the first column ( string )
 * @prop valueHeading - Heading for the value column ( string )
 * @prop total - Show a total row ( boolean, default: false )
 *
 * @cssprop [--border-color: var(--gray-200, #e9ecef)] - Row separator colour.
 * @cssprop [--heading-color: var(--gray-600, #868e96)] - Column heading colour.
 */
export default class DataTable extends ChartElement {
  static properties = {
    labelHeading: { type: String },
    valueHeading: { type: String },
    total: { type: Boolean },
  };

  static translations = translations;

  static styles = [ChartElement.styles, style];

  constructor() {
    super();
    this.total = false;
  }

  get firstHeading() {
    return this.labelHeading || this.t(this.timeseries ? 'period' : 'category');
  }

  shareOf(row) {
    if (!this.peak) return 0;

    return Number(
      ((Math.max(0, this.numberOf(row)) / this.peak) * 100).toFixed(2)
    );
  }

  renderBar(row, index) {
    const share = this.shareOf(row);

    // Zero draws an empty track. A `min-width` stub would read as a small
    // amount, which is the opposite of what an empty bucket means.
    return html`
      <td class="bar">
        <span class="track">
          ${share > 0
            ? html`<span
                class="fill"
                style="width: ${share}%; background-color: ${this.colorOf(
                  row,
                  index
                )}"></span>`
            : nothing}
        </span>
      </td>
    `;
  }

  render() {
    if (this.empty) return this.renderEmpty();

    return html`
      <table>
        ${this.caption
          ? html`<caption>
              ${this.caption}
            </caption>`
          : nothing}
        <thead>
          <tr>
            <th scope="col">${this.firstHeading}</th>
            <th scope="col" class="bar"></th>
            <th scope="col" class="numeric">
              ${this.valueHeading || this.t('value')}
            </th>
          </tr>
        </thead>
        <tbody>
          ${this.rows.map(
            (row, index) => html`
              <tr>
                <th scope="row">${this.labelOf(row)}</th>
                ${this.renderBar(row, index)}
                <td class="numeric">${this.valueOf(row)}</td>
              </tr>
            `
          )}
        </tbody>
        ${this.total
          ? html`
              <tfoot>
                <tr>
                  <th scope="row">${this.t('total')}</th>
                  <td class="bar"></td>
                  <td class="numeric">
                    ${formatValue(this.totalValue, {
                      locale: this.i18n.locale,
                      format: this.format,
                      precision: this.precision,
                    })}
                  </td>
                </tr>
              </tfoot>
            `
          : nothing}
      </table>
    `;
  }
}

DataTable.extendTranslationsAs('xs-data-table');

customElements.get('xs-data-table') ||
  customElements.define('xs-data-table', DataTable);
